/**
 * Orders Table Component
 * With sortable column headers and checkbox selection
 */

import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Checkbox,
  cn,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from '@ufc/uikit'
import { useTranslation, useSelector } from '@ufc/core'
import { selectUserRoleType } from '@ufc/domains/auth'
import { OrderRow } from './order-row'
import { ORDER_TABLE_COLUMNS } from '../constants'
import type { OrdersTableProps } from '../types'
import { useMemo } from 'react'
import type { OrdersSortBy, OrdersSortOrder } from '../../../../domain/types'
import type { OrderTableColumnKey } from '../types'

function buildVisibleColumnSet(columns: OrderTableColumnKey[]): Set<OrderTableColumnKey> {
  return new Set(columns)
}

function getVisibleColumns(
  showTenantColumn: boolean | undefined,
  visibleColumns: OrderTableColumnKey[]
) {
  const visibleColumnSet = buildVisibleColumnSet(visibleColumns)
  return ORDER_TABLE_COLUMNS.filter(
    (column) =>
      !column.requiresAdmin || Boolean(showTenantColumn)
  ).filter((column) => visibleColumnSet.has(column.key))
}

// Sortable header component
function SortableHeader({
  label,
  sortField,
  currentSortBy,
  currentSortOrder,
  onSort,
  align = 'start',
}: {
  label: string
  sortField?: OrdersSortBy
  currentSortBy?: OrdersSortBy
  currentSortOrder?: OrdersSortOrder
  onSort?: (field: OrdersSortBy, order: OrdersSortOrder) => void
  align?: 'start' | 'end'
}) {
  const isSorted = sortField && currentSortBy === sortField
  const isAsc = isSorted && currentSortOrder === 'asc'
  const isDesc = isSorted && currentSortOrder === 'desc'

  const handleClick = () => {
    if (!sortField || !onSort) return
    // Toggle: none -> desc -> asc -> desc
    const newOrder: OrdersSortOrder = isDesc ? 'asc' : 'desc'
    onSort(sortField, newOrder)
  }

  if (!sortField || !onSort) {
    return (
      <span className={cn('block', align === 'end' && 'text-end')}>
        {label}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'flex items-center gap-1.5 hover:text-foreground transition-colors cursor-pointer w-full',
        align === 'end' && 'justify-end',
        isSorted ? 'text-foreground' : 'text-muted-foreground'
      )}
    >
      <span>{label}</span>
      <span className={cn(
        'transition-opacity',
        isSorted ? 'opacity-100' : 'opacity-40 hover:opacity-70'
      )}>
        {isAsc ? (
          <ArrowUp className="h-3.5 w-3.5" />
        ) : isDesc ? (
          <ArrowDown className="h-3.5 w-3.5" />
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5" />
        )}
      </span>
    </button>
  )
}

export function OrdersTable({
  orders,
  selectedRowId,
  isRTL,
  showTenantColumn,
  visibleColumns,
  sortBy,
  sortOrder,
  onSortChange,
  onRowClick,
  onRowDoubleClick,
  onCancel,
  onDownloadPDF,
  // Checkbox selection props
  selectedOrderIds = [],
  onToggleOrderSelection,
  onToggleSelectAll,
  onToggleSelectAllAnyStatus,
  isAllOnPageSelected = false,
  isSomeOnPageSelected = false,
  isAllOnPageSelectedAnyStatus = false,
  isSomeOnPageSelectedAnyStatus = false,
  showCheckboxes = false,
}: OrdersTableProps) {
  const { t } = useTranslation()
  const roleType = useSelector(selectUserRoleType)
  const visibleColumnSet = useMemo(() => buildVisibleColumnSet(visibleColumns), [visibleColumns])

  const isAnyStatusSelectionActive = Boolean(
    onToggleSelectAllAnyStatus || isAllOnPageSelectedAnyStatus || isSomeOnPageSelectedAnyStatus
  )
  const checkboxChecked = isAnyStatusSelectionActive ? isAllOnPageSelectedAnyStatus : isAllOnPageSelected
  const checkboxIndeterminate = isAnyStatusSelectionActive ? isSomeOnPageSelectedAnyStatus : isSomeOnPageSelected
  const handleToggleSelectAll = isAnyStatusSelectionActive
    ? onToggleSelectAllAnyStatus
    : onToggleSelectAll

  const columns = getVisibleColumns(showTenantColumn, visibleColumns)

  return (
    <div data-testid="orders-table" className="glass-card rounded-2xl overflow-hidden border border-border/50">
      <Table>
        <TableHeader>
          <TableRow className="border-border/40 hover:bg-transparent bg-muted/50">
            {showCheckboxes && (
              <TableHead className="px-3 py-4 w-[50px]">
                <Checkbox
                  checked={checkboxChecked}
                  ref={(el) => {
                    if (el) {
                      (el as HTMLButtonElement).dataset.state = checkboxIndeterminate ? 'indeterminate' : checkboxChecked ? 'checked' : 'unchecked'
                    }
                  }}
                  onCheckedChange={() => handleToggleSelectAll?.()}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:border-primary"
                />
              </TableHead>
            )}
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  'text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider',
                  col.width,
                  col.align === 'end' && 'text-end'
                )}
              >
                <SortableHeader
                  label={t(col.labelKey)}
                  sortField={col.sortField}
                  currentSortBy={sortBy}
                  currentSortOrder={sortOrder}
                  onSort={onSortChange}
                  align={col.align}
                />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <OrderRow
              key={order.id}
              order={order}
              isSelected={selectedRowId === order.id}
              isRTL={isRTL}
              showTenantColumn={showTenantColumn}
              visibleColumns={visibleColumns}
              onClick={onRowClick}
              onDoubleClick={onRowDoubleClick}
              onCancel={onCancel}
              onDownloadPDF={onDownloadPDF}
              showCheckbox={showCheckboxes}
              isChecked={selectedOrderIds.includes(order.id)}
              onCheckChange={(orderId) => onToggleOrderSelection?.(orderId)}
              roleType={roleType}
              visibleColumnSet={visibleColumnSet}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
